import { BrowserStorage } from '@atc/browser-storage';
import WalletStorageFetcher from './WalletStorageFetcher.js';

/**
 * @module WalletStorageService
 */
class WalletStorageService {
  /**
   * @param {Function} fetcher fetcher for the wallet-storage-service
   */
  constructor() {
    let {
      environment = 'staging',
      getConsumerId
    } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    this.environment = environment;
    this.getConsumerid = getConsumerId;
    this.consumeridCache = null;
    this.walletIdCache = new BrowserStorage('WALLET_ID', {
      persist: true,
      deserialize: value => value // Override bc JSON.parse on a string throws an error
    });
    this.walletLastUpdatedCache = new BrowserStorage('WALLET_LAST_UPDATED', {
      persist: true,
      deserialize: value => value // Override bc JSON.parse on a string throws an error
    });
  }
  setConsumerid(id) {
    this.consumeridCache = id;
  }
  resetConsumerid() {
    this.setConsumerid(null);
  }
  async fetchConsumerid() {
    let isLoggedIn = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
    if (!isLoggedIn) {
      return null;
    }
    if (this.consumeridCache) {
      return this.consumeridCache;
    }
    if (this.getConsumerid) {
      const consumerid = await this.getConsumerid();
      if (consumerid) {
        cachedConsumerid = consumerid;
        return consumerid;
      }
    }
    return null;
  }
  getUserConsumerid() {
    return this.consumeridCache;
  }
  setWalletId(walletId) {
    if (walletId) {
      this.walletIdCache.setItem(walletId);
    }
  }
  getWalletId() {
    const walletId = this.walletIdCache.getItem();
    return walletId || null;
  }
  setWalletLastUpdated(date) {
    this.walletLastUpdatedCache.setItem(date);
  }
  getWalletLastUpdated() {
    return this.walletLastUpdatedCache.getItem();
  }
  async setData(data) {
    const body = {
      ...data
    };
    const consumerid = this.getUserConsumerid();
    if (consumerid) {
      body.consumerid = consumerid;
    }
    const response = await WalletStorageFetcher({
      method: 'POST',
      body,
      environment: this.environment
    });
    return response;
  }
  async getData(payload) {
    if (payload?.id || payload?.consumerid) {
      try {
        const {
          body,
          statusCode,
          success
        } = await WalletStorageFetcher({
          method: 'GET',
          environment: this.environment,
          ...payload
        });
        const response = {
          paymentInfo: body?.paymentInfo,
          statusCode,
          success: statusCode === 404 ? true : success // NOTE: We don't want to show an error if the user doesn't yet have a wallet id
        };
        if (response?.paymentInfo && !response?.paymentInfo?.walletId && body?.id) {
          response.paymentInfo.walletId = body?.id;
        }
        return response;
      } catch (error) {
        return {};
      }
    }
    return {};
  }
  async getDataWithWalletId() {
    const walletId = this.getWalletId();
    if (walletId) {
      return this.getData({
        id: walletId
      });
    }
    return {};
  }
  async getDataWithConsumerid() {
    const consumerid = this.getUserConsumerid();
    if (consumerid) {
      return this.getData({
        consumerid
      });
    }
    return {};
  }

  /**
   * @alias module:WalletStorageService
   * @param walletId
   * @param shouldInit
   * @param paymentsInfo
   * @return {Promise}
   */
  async updateData(_ref) {
    let {
      paymentsInfo
    } = _ref;
    const body = {
      paymentInfo: paymentsInfo
    };
    const walletId = this.getWalletId();
    const consumerid = this.getUserConsumerid();
    if (consumerid) {
      body.consumerid = consumerid;
    }
    if (walletId && paymentsInfo) {
      const {
        statusCode,
        success
      } = await WalletStorageFetcher({
        method: 'PUT',
        id: walletId,
        body,
        environment: this.environment
      });

      // cache last updated date
      if (paymentsInfo?.lastUpdated > 0) {
        this.setWalletLastUpdated(paymentsInfo?.lastUpdated);
      }
      return {
        statusCode,
        success
      };
    }
    return {
      success: true
    };
  }

  /**
   * @alias module:WalletStorageService
   * @param {*} onMessageReceived
   */
  syncId() {
    return new Promise(resolve => {
      this.syncIframe.ensureIframe(
      // if syncId is attempted more than once,
      // it means we have an invalid id in wallet central iframe
      // to prevent infinite loop, return null so a new id is generated
      () => resolve(null), function () {
        let data = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
        return resolve(data);
      });
    });
  }
  postId(id) {
    const callback = () => this.syncIframe.postMessageToIframe({
      type: 'wallet-sync-set-id',
      id
    });
    this.syncIframe.ensureIframe(callback, callback);
  }
}
export { WalletStorageService as default };
